import { now, user } from 'src/StateObserver';
import { getGameScene } from 'src/game/minigame/match2-timestep/game/getGameScene';
import { getEntryData } from 'src/lib/platformUtils';
import {
  dreamDriftSchedule,
  dreamDriftUnlockLevel,
} from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import features from 'src/replicant/ruleset/features';
import type { State } from 'src/replicant/State';

/**
 * Detect a Dream Drift resurrection OA entry by its asset during an ongoing event.
 *
 * @param state Player state used to check feature availability.
 * @returns Whether the feature is enabled, the asset identifies Dream Drift, and an event is active.
 */
export function isDreamDriftEntry(state: State): boolean {
  // Disabled players cannot qualify for a Dream Drift resurrection landing.
  if (!features(state).dreamDrift) return false;

  /** Case-normalized OA asset identifier, empty when no creative was supplied. */
  const creativeId = getEntryData().$creativeAssetID?.toLowerCase() ?? '';

  // The creative identifies the entry without relying on a deep link or subfeature.
  if (!creativeId.includes('dreamdrift')) {
    return false;
  }

  // Stale links outside an event window do not qualify as Dream Drift landings.
  return !!dreamDriftSchedule.getCurrentEvent(now());
}

/**
 * Check whether this OA entry should preview the level-locked current event.
 *
 * @returns Whether the player entered through a valid Dream Drift resurrection OA and is below the unlock level.
 */
export function dreamDriftIsResurrectionLocked(): boolean {
  // Only active OA landings can preview the event.
  if (!isDreamDriftEntry(user())) return false;

  // Stop showing the lock as soon as the player reaches the configured level.
  return user().match2.level < dreamDriftUnlockLevel;
}

/**
 * Check whether Dream Drift has an unfinished attempt or still owns the game scene.
 *
 * @returns Whether Dream Drift is starting, playing, or awaiting scene teardown.
 */
export function dreamDriftIsPlaying(): boolean {
  // State covers startup; the scene covers the interval after the attempt finishes.
  return (
    (user().dreamDrift?.instance?.playing ?? 0) > 0 ||
    getGameScene()?.isDreamDrift === true
  );
}
